import { html, LitElement } from 'lit';
import { debounce } from '../utilities/utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

class WishlistButton extends LitElement {
  static get properties() {
    return {
      productId: {
        type: String,
        attribute: 'product-id',
        reflect: true,
      },
      productImage: { type: String, attribute: 'product-image' },
      inWishlist: { type: Boolean, state: true },
      isAnimating: { type: Boolean, state: true },
      wishlistCount: { type: Number, state: true },
      isUserInteracting: { type: Boolean, state: true },
      productTitle: { type: String, attribute: 'product-title' },
      productCategory: { type: String, attribute: 'product-category' },
      wishlistId: { type: Number, attribute: 'wishlist-id' },
    };
  }

  createRenderRoot() {
    return this;
  }

  get sessionBoardOrCurrentId() {
    return this.wishlistId || window.sessionStorage.getItem('wishlist_adding_board') || window.wishlist.BOARD_CONFIG.DEFAULT_BOARD_ID;
  }

  async firstUpdated() {
    super.firstUpdated();
    this.isAnimating = false;
    this.isUserInteracting = false;
    this.updateWishlistState();
    this.wishlist = await window.wishlist.retrieve();
    this.wishlistCount = this.wishlist.count || 0;

    this.boundToggleWishlist = this.toggleWishlist.bind(this);
    this.addEventListener('click', this.boundToggleWishlist, { capture: true });

    window.eventKeeper?.onEventHeard('wishlist:initialise', (detail) => {
      this.updateWishlistFromEvent({ detail });
    });

    this.boundUpdateWishlistFromEvent = this.updateWishlistFromEvent.bind(this);
    document.addEventListener('wishlist:initialise', this.boundUpdateWishlistFromEvent);
    document.addEventListener('wishlist:update', this.boundUpdateWishlistFromEvent);
    document.addEventListener('wishlist:remove', this.boundUpdateWishlistFromEvent);

    // Remove limit exceeded handling since limits are removed

    this.debouncedWishlistApiCall = debounce(this.performWishlistApiCall.bind(this), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.boundToggleWishlist) {
      this.removeEventListener('click', this.boundToggleWishlist);
    }

    if (this.boundUpdateWishlistFromEvent) {
      document.removeEventListener('wishlist:initialise', this.boundUpdateWishlistFromEvent);
      document.removeEventListener('wishlist:update', this.boundUpdateWishlistFromEvent);
      document.removeEventListener('wishlist:remove', this.boundUpdateWishlistFromEvent);
    }
  }

  async updateWishlistState() {
    try {
      this.inWishlist = await window.wishlist.productIsIn(this.productId);
    } catch (error) {
      console.error('Error updating wishlist state:', error);
    }
  }

  handleChangeWishlist() {
    const addingBoard = this.sessionBoardOrCurrentId;
    const currentBoard = wishlist.boards.find((board) => board.id === parseInt(addingBoard)) || window.wishlist.boards.find((board) => board.id === 1);

    const event = new CustomEvent('modal-wishlist-move-to-board:open', {
      detail: {
        items: [{ productId: this.productId }],
        currentBoard,
        showAddingBoard: true,
      },
    });
    document.dispatchEvent(event);

    window.toast.hide();
  }

  toastMessage() {
    const addingBoard = this.sessionBoardOrCurrentId;

    const currentBoard = window.wishlist?.boards.find((board) => board.id === parseInt(addingBoard))?.name || window.wishlist.boards.find((board) => board.id === window.wishlist.BOARD_CONFIG.DEFAULT_BOARD_ID)?.name || window.wishlist.BOARD_CONFIG.DEFAULT_BOARD_NAME;

    return html`
      <div class="wishlist_add_toast">
        <div class="wishlist_add_toast_image">
          <img src=${this.productImage} width="50" height="75" alt="${this.productTitle}" loading="eager" />
        </div>
        <span class="wishlist_add_toast_message">${unsafeHTML(window.theme.strings.wishlist.saved_to)}${' '}<a href="${window.Shopify.routes.root}pages/my-wishlist?board=${addingBoard}" class="wishlist_add_toast_name">${currentBoard}</a></span>
        <button class="wishlist_add_toast_change" @click=${this.handleChangeWishlist.bind(this)} type="button">${unsafeHTML(window.theme.strings.wishlist.change)}</button>
      </div>
    `;
  }

  updateWishlistFromEvent(event) {
    if (!this.isUserInteracting) {
      this.inWishlist = event.detail.allProductIds && event.detail.allProductIds[this.productId] === true;
    }

    this.wishlistCount = event.detail.count || 0;
    this.requestUpdate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'product-id' && oldValue !== newValue) {
      this.updateWishlistState();
    }
  }

  async toggleWishlist() {
    if (!this.productId) {
      return;
    }

    this.isUserInteracting = true;

    const newWishlistState = !this.inWishlist;
    this.inWishlist = newWishlistState;

    this.triggerHeartPulseAnimation(
      newWishlistState
        ? 'in'
        : 'out',
    );

    this.debouncedWishlistApiCall(newWishlistState);

    if (window.wishlist.boards.length === 1 || !window.Shopify?.customer_logged_in) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const hasBoardInParams = params.get('board');

    const onWishlistSingleBoardView = window.location.pathname.includes('/my-wishlist') && hasBoardInParams;

    if (this.inWishlist && !onWishlistSingleBoardView) {
      window.toast(this.toastMessage());
    } else {
      window.toast.hide();
    }
  }

  async performWishlistApiCall(addToWishlist) {
    const sessionBoardId = this.sessionBoardOrCurrentId;
    const boardId = window.wishlist.boards.find((board) => board.id === parseInt(sessionBoardId))?.id || window.wishlist.BOARD_CONFIG.DEFAULT_BOARD_ID;

    try {
      if (addToWishlist) {
        await window.wishlist.add(this.productId, boardId);
        window.dispatchEvent(
          new CustomEvent('analytics:gtag', {
            detail: {
              event: 'wishlist_add',
              params: {
                ...(this.productTitle ? { wishlist_item: this.productTitle } : {}),
                ...(this.productCategory ? { wishlist_item_category: this.productCategory } : {}),
                product_id: String(this.productId),
              },
            },
          })
        );
        window.announcements?.info(`Added To Wishlist ${this.productTitle}`);
      } else {
        if (this.wishlistId) {
          await window.wishlist.remove(this.productId, boardId);
        } else {
          await window.wishlist.removeFromAllBoards(this.productId);
        }
        window.dispatchEvent(
          new CustomEvent('analytics:gtag', {
            detail: {
              event: 'wishlist_remove',
              params: {
                ...(this.productTitle ? { wishlist_item: this.productTitle } : {}),
                ...(this.productCategory ? { wishlist_item_category: this.productCategory } : {}),
                product_id: String(this.productId),
              },
            },
          })
        );
        window.announcements?.info(`Removed From Wishlist ${this.productTitle}`);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      this.inWishlist = !addToWishlist;
    } finally {
      setTimeout(() => {
        this.isUserInteracting = false;
      }, 100);
    }
  }

  triggerHeartPulseAnimation(type = 'in') {
    const button = this.querySelector('.wishlist_button_btn');

    if (button) {
      button.classList.remove('pulse_animation_in', 'pulse_animation_out');

      button.offsetHeight;

      const animationClass =
        type === 'in'
          ? 'pulse_animation_in'
          : 'pulse_animation_out';
      button.classList.add(animationClass);

      this.isAnimating = true;

      setTimeout(() => {
        this.isAnimating = false;
      }, 150);

      setTimeout(() => {
        button.classList.remove(animationClass);
      }, 300);
    }
  }

  render() {
    const ariaLabel = this.inWishlist
      ? window.theme.strings.wishlist.remove_from_wishlist.replace('{{ product_id }}', this.productTitle || this.productId)
      : window.theme.strings.wishlist.add_to_wishlist.replace('{{ product_id }}', this.productTitle || this.productId);

    return html`
      <button class="wishlist_button_btn" title="${ariaLabel}" aria-label="${ariaLabel}">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.9932 5.13581C9.9938 2.7984 6.65975 2.16964 4.15469 4.31001C1.64964 6.45038 1.29697 10.029 3.2642 12.5604C4.89982 14.6651 9.84977 19.1041 11.4721 20.5408C11.6536 20.7016 11.7444 20.7819 11.8502 20.8135C11.9426 20.8411 12.0437 20.8411 12.1361 20.8135C12.2419 20.7819 12.3327 20.7016 12.5142 20.5408C14.1365 19.1041 19.0865 14.6651 20.7221 12.5604C22.6893 10.029 22.3797 6.42787 19.8316 4.31001C17.2835 2.19216 13.9925 2.7984 11.9932 5.13581Z"
            fill="${this.inWishlist || this.isAnimating
        ? 'currentColor'
        : 'none'}"
            stroke="currentColor"
            stroke-width="${this.inWishlist
        ? 0
        : 2}"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    `;
  }
}

window.customElements.get('wishlist-button') || window.customElements.define('wishlist-button', WishlistButton);
